import type { AppLanguage } from '../i18n/types';
import type { MessageKey } from '../i18n/messages';
import {
  isEmailLikeDisplayLabel,
  isUnusableUserFacingLabel,
} from '../utils/headerDisplayName';

type TranslateFn = (key: MessageKey, params?: Record<string, string | number>) => string;

type SearchActionSource = {
  summaryKey?: MessageKey;
  summaryParams?: Record<string, string | number>;
  summaryText?: string;
};

/** Prefer stored first name; else first token of a usable display name. */
export function resolveAssistantSpeakFirstName(
  firstNameHint: string | null | undefined,
  displayName: string | null | undefined,
): string | null {
  const hint = firstNameHint?.trim();
  if (hint && !isEmailLikeDisplayLabel(hint) && !isUnusableUserFacingLabel(hint)) {
    return hint;
  }
  const display = displayName?.trim();
  if (!display || isEmailLikeDisplayLabel(display) || isUnusableUserFacingLabel(display)) {
    return null;
  }
  const token = display.split(/\s+/)[0]?.trim();
  if (!token || isEmailLikeDisplayLabel(token) || isUnusableUserFacingLabel(token)) {
    return null;
  }
  return token;
}

export function formatAssistantOpenGreeting(
  firstName: string | null | undefined,
  t: TranslateFn,
): string {
  const name = firstName?.trim();
  if (name) {
    return t('assistant.voice.greeting', { name });
  }
  return t('assistant.voice.greetingAnonymous');
}

/** Voice-friendly type label (e.g. Hebrew open space → עמדות אופן ספייס). */
export function formatAssistantVoiceTypeLabel(
  type: string | null | undefined,
  language: AppLanguage,
  t: TranslateFn,
): string {
  const raw = type?.trim() || '';
  if (!raw) {
    return '';
  }
  if (
    language === 'he' &&
    (/אופן\s*ספייס/i.test(raw) || /מרחב\s*פתוח/i.test(raw) || /open\s*space/i.test(raw))
  ) {
    return t('assistant.voice.typeOpenSpace');
  }
  if (language === 'en' && /open\s*space/i.test(raw)) {
    return t('assistant.voice.typeOpenSpace');
  }
  return raw;
}

/**
 * Spoken “what I’m doing” line for a search — not a raw echo of the user utterance.
 * Example HE: מחפש עבורך עמדות אופן ספייס בתל אביב
 */
export function formatAssistantSearchAction(
  source: SearchActionSource,
  language: AppLanguage,
  t: TranslateFn,
): string {
  if (source.summaryText?.trim()) {
    return source.summaryText.trim();
  }
  const key = source.summaryKey;
  const params = { ...(source.summaryParams ?? {}) };
  if (typeof params.type === 'string') {
    params.type = formatAssistantVoiceTypeLabel(params.type, language, t);
  }
  switch (key) {
    case 'assistant.result.searchTypeCity':
      return t('assistant.voice.actionSearchTypeCity', params);
    case 'assistant.result.searchType':
    case 'assistant.result.browseType':
      return t('assistant.voice.actionSearchType', params);
    case 'assistant.result.searchCity':
      return t('assistant.voice.actionSearchCity', params);
    case 'assistant.result.searchTypeSlot':
      return t('assistant.voice.actionSearchTypeSlot', params);
    case 'assistant.result.searchSlot':
      return t('assistant.voice.actionSearchSlot', params);
    case 'assistant.result.refine':
      return t('assistant.voice.actionRefine', params);
    case 'assistant.result.search':
      return t('assistant.voice.actionSearch', params);
    default:
      if (key) {
        return t(key, params);
      }
      return '';
  }
}
